import fs from "fs-extra";

import { AppArg, Application, EnvArg, EnvironmentType, getApplications, groupByTeam, type Environment } from "../app/app.ts";
import { Edit } from "../app/edit/edit.ts";
import type { Directory } from "../filesync/directory.ts";
import { SyncJsonState } from "../filesync/sync-json-state.ts";
import colors from "../output/colors.ts";
import { select } from "../output/select.ts";
import { setSentryTags } from "../output/sentry.ts";
import { sprint } from "../output/sprint.ts";
import { getUserOrLogin } from "../user/user.ts";
import { sortBySimilar } from "../util/collection.ts";
import type { Command } from "./command.ts";
import type { Context } from "./context.ts";
import { FlagError, type FlagsDefinition, type FlagsResult } from "./flag.ts";

export { AppArg, EnvArg } from "../app/app.ts";

/**
 * Combined flags definition for --app and --env flags used by commands
 * that need to identify a Gadget application and environment.
 */
export const AppIdentityFlags = {
  "--application": AppArg,
  "--environment": EnvArg,
} satisfies FlagsDefinition;

export type AppIdentityFlags = typeof AppIdentityFlags;
export type AppIdentityFlagsResult = FlagsResult<AppIdentityFlags>;

/**
 * An object that knows what app and environment we are working with.
 */
export class AppIdentity {
  /**
   * The {@linkcode Edit} client that can be used to send Gadget API
   * requests to the environment that the directory is synced to.
   */
  readonly edit: Edit;

  /**
   * The {@linkcode Context} that was used to initialize this
   * {@linkcode AppIdentity} instance.
   */
  readonly ctx: Context;

  /**
   * The parsed {@linkcode AppIdentityFlags} that the user passed to ggt.
   */
  readonly flags: AppIdentityFlagsResult;

  /**
   * The {@linkcode Environment} we are working with.
   */
  readonly environment: Environment;

  /**
   * The state of the `.gadget/sync.json` file on the local
   * filesystem, if found.
   */
  readonly syncJsonState?: SyncJsonState;

  private constructor(ctx: Context, flags: AppIdentityFlagsResult, environment: Environment, syncJsonState?: SyncJsonState) {
    this.ctx = ctx.child({ name: "app-identity" });
    this.flags = flags;
    this.environment = environment;
    this.syncJsonState = syncJsonState;

    this.edit = new Edit(this.ctx, this.environment);
  }

  /**
   * Loads a {@linkcode AppIdentity} from the specified directory, or using the command line flags. Always returns an identity by using the given input, or by asking the user if not enough information is provided.
   *
   * - Ensures a user is logged in.
   * - Ensures the user has at least one application.
   * - Uses the app/env from the local .gadget/sync.json file if it exists
   * - Uses the app/env from the command line flags if provided
   * - Asks the user to select an app/env if not enough information is provided
   */
  static async load(
    ctx: Context,
    { command, flags, directory }: { command: Command; flags: AppIdentityFlagsResult; directory: Directory },
  ): Promise<AppIdentity> {
    ctx = ctx.child({ name: "app-identity" });

    const user = await getUserOrLogin(ctx, command);
    const availableApps = await getApplications(ctx);
    if (availableApps.length === 0) {
      throw new FlagError(
        sprint`
          You (${user.email}) don't have have any Gadget applications.

          Visit https://gadget.new to create one!
        `,
        { usageHint: false },
      );
    }

    // try to load the .gadget/sync.json file
    const syncJsonFile = await fs
      .readFile(directory.absolute(".gadget/sync.json"), "utf8")
      .catch((error: unknown) => ctx.log.warn("failed to read .gadget/sync.json", { error }));
    let state: SyncJsonState | undefined;

    if (syncJsonFile) {
      // the .gadget/sync.json file exists, try to parse it
      try {
        state = SyncJsonState.parse(JSON.parse(syncJsonFile));
      } catch (error) {
        // the .gadget/sync.json file exists, but it's invalid
        ctx.log.warn("failed to parse .gadget/sync.json", { error, syncJsonFile });
      }
    }

    const application = await loadApplication({ flags, availableApps, state });
    const environment = await loadEnvironment({ command, flags, application, state });

    setSentryTags({
      application_id: application.id,
      environment_id: environment.id,
    });

    return new AppIdentity(ctx, flags, environment, state);
  }

  get application(): Application {
    return this.environment.application;
  }
}

const AllowedProdCommands: Command[] = ["pull", "logs", "eval", "var"] satisfies Command[];

/**
 * Resolves the application from flags, sync.json state, or interactive prompt.
 */
export const loadApplication = async ({
  flags,
  availableApps,
  state,
  selectPrompt = "Which application do you want to develop?",
}: {
  flags: { "--application"?: string };
  availableApps: Application[];
  state?: SyncJsonState;
  selectPrompt?: string;
}): Promise<Application> => {
  let appSlug = flags["--application"] || state?.application;
  if (!appSlug) {
    // the user didn't specify an app, ask them to select one
    const groupedChoices: [string, string[]][] = Array.from(groupByTeam(availableApps)).map(([teamName, apps]) => [
      teamName,
      apps.map((app) => app.slug),
    ]);

    appSlug = await select({
      groupedChoices,
      searchable: true,
      content: selectPrompt,
    });
  }

  const application = availableApps.find((app) => app.slug === appSlug);
  if (application) {
    // the user specified an app or we loaded it from the state,
    // and it exists in their list of applications, so return it
    return application;
  }

  // the specified appSlug doesn't exist in their list of apps,
  // either they misspelled it or they don't have access to it
  // anymore, suggest some apps that are similar to the one they
  // specified
  const similarAppSlugs = sortBySimilar(
    appSlug,
    availableApps.map((app) => app.slug),
  ).slice(0, 5);

  // TODO: differentiate between incorrect --app vs state.application?
  throw new FlagError(
    sprint`
      Unknown application:

        ${appSlug}

      Did you mean one of these?

        ${similarAppSlugs.map((s) => `• ${s}`).join("\n")}
    `,
    { usageHint: false },
  );
};

const loadEnvironment = async ({
  command,
  flags,
  application,
  state,
}: {
  command: Command;
  flags: AppIdentityFlagsResult;
  application: Application;
  state?: SyncJsonState;
}): Promise<Environment> => {
  const selectableEnvironments = application.environments.filter((env) => env.type === EnvironmentType.Development);
  if (AllowedProdCommands.includes(command)) {
    // allow pulling from production environments
    selectableEnvironments.push(...application.environments.filter((env) => env.type === EnvironmentType.Production));
  }

  let selectedEnvironment = flags["--environment"] || state?.environment;
  if (!selectedEnvironment) {
    // user didn't specify an environment, ask them to select one
    selectedEnvironment = await select({
      choices: selectableEnvironments.map((env) => env.name),
      content: "Which environment do you want to develop on?",
    });
  }

  if (selectedEnvironment.toLowerCase() === "production" && !AllowedProdCommands.includes(command)) {
    // specifically call out that they can't dev, push, etc. to prod
    throw new FlagError(
      sprint`
        You cannot "ggt ${command}" your ${colors.identifier("production")} environment.
      `,
      { usageHint: false },
    );
  }

  const environment = selectableEnvironments.find((env) => env.name === selectedEnvironment.toLowerCase());
  if (environment) {
    // the user specified an environment or we loaded it from the state,
    // and it exists in the app's list of environments, so return it
    return { ...environment, application };
  }

  // the specified env doesn't exist in their list of environments,
  // either they misspelled it or they don't have access to it
  // anymore, suggest some envs that are similar to the one they
  // specified
  const similarEnvironments = sortBySimilar(
    selectedEnvironment,
    selectableEnvironments.map((env) => env.name),
  ).slice(0, 5);

  throw new FlagError(
    sprint`
      Unknown environment:

        ${selectedEnvironment}

      Did you mean one of these?

        ${similarEnvironments.map((s) => `• ${s}`).join("\n")}
    `,
    { usageHint: false },
  );
};
